import React, { type FC, useMemo } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import { useDebouncedInputChange } from 'Hooks/useDebouncedInputChange'
import type { ModalCopyState } from 'Pages/GroupsList/model'
import {
  modalCopyChangeFieldCallback,
  modalCopyCheckNameCallback,
} from 'Pages/GroupsList/recoil/callbacks/modals'
import {
  listsOptionsState,
  modalCopyNameInvalidSelector,
  modalCopyNameNotUniqueSelector,
  modalCopyState,
  userVisibleListsState,
} from 'Pages/GroupsList/recoil/state/state'
import {
  Flex,
  FormElement,
  INPUT_TEXT_VARIANT_DANGER,
  InputText,
  MESSAGE_BOX_VARIANT_HELP,
  MessageBox,
  ModalBody,
  ModalHead,
  Select,
} from 'mailup-web-components'

const ModalCopyFormStep: FC = () => {
  const intl = useIntl()

  const { group, newList, newName } = useRecoilValue(modalCopyState)
  const options = useRecoilValue(listsOptionsState)
  const lists = useRecoilValue(userVisibleListsState)
  const isNameInvalid = useRecoilValue(modalCopyNameInvalidSelector)
  const isNameNotUnique = useRecoilValue(modalCopyNameNotUniqueSelector)

  const changeField = useRecoilCallback(modalCopyChangeFieldCallback, [])
  const checkName = useRecoilCallback(modalCopyCheckNameCallback, [])

  const handleChangeField =
    (f: keyof ModalCopyState) =>
    (value: unknown): void => {
      changeField({
        [f]: value,
      })
    }
  const handleCheckName =
    (f: keyof ModalCopyState) =>
    (value: unknown): Promise<unknown> => {
      return checkName({
        [f]: value,
      })
    }

  const handleChangeList = (value: number) => {
    const selectedList = lists.find((list) => list.IdList === value)
    handleChangeField('newList')(selectedList)
    handleCheckName('newList')(selectedList)
  }

  const { handleInputChange: handleChangeName } = useDebouncedInputChange(
    handleCheckName('newName'),
    handleChangeField('newName'),
  )

  return (
    <>
      <ModalHead
        title={intl.formatMessage(
          {
            id: 'Load.CopyGroupToList.FormHead',
          },
          { groupName: group?.Name },
        )}
      />
      <ModalBody>
        <Flex flexDirection="column" gap="16px">
          <FormElement
            label={intl.formatMessage({
              id: 'Load.CopyGroupToList.ListSelect',
            })}
            required
            labelFor="modal-copy__select-list"
          >
            <Select
              searchable
              options={options}
              value={newList?.IdList}
              onChange={handleChangeList}
              data-mu-test="modal-copy__select-list"
              id="modal-copy__select-list"
            />
          </FormElement>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.CopyGroupToList.NewNameInput',
            })}
            required
            feedback={
              isNameNotUnique
                ? intl.formatMessage({
                    id: 'Load.CopyGroupToList.NewNameNotUnique',
                  })
                : undefined
            }
            labelFor="modal-copy__input-name"
          >
            <InputText
              value={newName}
              onChange={handleChangeName}
              data-mu-test="modal-copy__input-name"
              id="modal-copy__input-name"
              variant={isNameInvalid ? INPUT_TEXT_VARIANT_DANGER : undefined}
            />
          </FormElement>
          <MessageBox variant={MESSAGE_BOX_VARIANT_HELP}>
            {intl.formatMessage({
              id: 'Load.CopyGroupToList.MsgBox',
            })}
          </MessageBox>
        </Flex>
      </ModalBody>
    </>
  )
}

export default ModalCopyFormStep
